import {
  createPrimitiveNode,
  createRectangleNode,
  createTextNode,
} from "@/document/model/document";
import type { GroupNode, SceneNode, TextNode, VectorNetworkNode } from "@/document/model/nodes";
import { createIdentityTransform } from "@/document/model/nodes";
import { createDefaultStyle, normalizeImagePaint } from "@/document/model/styles";
import { getVectorNetworkBounds, sampleNetworkRegionContours } from "@/document/operations/vectorGeometry";
import { pathDataToVectorNetwork } from "@/document/operations/pathDataToVectorNetwork";
import { promoteVectorRegions } from "@/document/operations/vectorRegions";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { fontCatalogFamilies } from "@/lib/fontCatalog";
import { createId } from "@/lib/ids";
import type { Rect } from "@/lib/math/rect";
import { SvgImportIssueCollector, type SvgImportIssue } from "./issues";
import {
  childElements,
  elementLocalName,
  parseSvgDocument,
  readSvgLength,
  readSvgNumberList,
  svgRootElement,
} from "./parseSvgDocument";
import {
  applySvgMatrix,
  IDENTITY_SVG_MATRIX,
  multiplySvgMatrices,
  parseSvgTransformAttribute,
  type SvgTransformMatrix,
} from "./svgTransforms";
import {
  createDefaultSvgPresentationContext,
  mergeSvgPresentationContext,
  nodeStyleFromSvgElement,
  nodeStyleFromSvgPresentation,
  resolveSvgFontFamily,
  type SvgPresentationContext,
} from "./svgStyles";
import { SvgDefsRegistry } from "./svgDefs";
import { parseBackgroundBlurFromForeignObject } from "./svgEffects";
import type { BackgroundBlurEffect } from "@/document/model/styles";
import { imagePaintFromImageElement } from "./svgImages";

export interface SvgImportResult {
  nodes: SceneNode[];
  bounds: Rect;
  issues: SvgImportIssue[];
}

export interface SvgImportOptions {
  name?: string;
}

export function importSvgMarkup(markup: string, options: SvgImportOptions = {}): SvgImportResult {
  const issues = new SvgImportIssueCollector();
  const doc = parseSvgDocument(markup, issues);
  const root = svgRootElement(doc);
  const viewBox = readViewBox(root);
  const width = readSvgLength(root.getAttribute("width"), viewBox?.width ?? 100);
  const height = readSvgLength(root.getAttribute("height"), viewBox?.height ?? 100);
  const rootOffset = viewBox ? { x: viewBox.x, y: viewBox.y } : { x: 0, y: 0 };
  const rootMatrix = parseSvgTransformAttribute(root.getAttribute("transform"));
  const defs = SvgDefsRegistry.fromDocument(root);
  const presentation = mergeSvgPresentationContext(createDefaultSvgPresentationContext(), root, issues);
  const children = importSvgChildren(
    childElements(root),
    multiplySvgMatrices(rootMatrix, { a: 1, b: 0, c: 0, d: 1, e: -rootOffset.x, f: -rootOffset.y }),
    presentation,
    defs,
    issues,
    { skipCurvaExportFrameBackground: true },
  );

  const nodes = wrapImportedNodes(children, options.name ?? "SVG");
  const bounds = unionSceneNodeBounds(nodes) ?? { x: 0, y: 0, width, height };
  return { nodes, bounds, issues: issues.issues };
}

interface SvgImportChildrenOptions {
  skipCurvaExportFrameBackground?: boolean;
}

function importSvgChildren(
  elements: Element[],
  parentMatrix: SvgTransformMatrix,
  parentPresentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  options: SvgImportChildrenOptions = {},
): SceneNode[] {
  const nodes: SceneNode[] = [];
  let pendingBackgroundBlur: BackgroundBlurEffect | undefined;

  for (const element of elements) {
    const localName = elementLocalName(element);
    if (localName === "foreignobject") {
      pendingBackgroundBlur = parseBackgroundBlurFromForeignObject(element) ?? pendingBackgroundBlur;
      continue;
    }
    if (localName === "defs" || localName === "clippath" || localName === "mask" || localName === "title" || localName === "desc" || localName === "metadata") {
      continue;
    }
    if (options.skipCurvaExportFrameBackground && isCurvaExportFrameBackground(element)) {
      continue;
    }

    const presentation = mergeSvgPresentationContext(parentPresentation, element, issues);
    if (!presentation.display) {
      continue;
    }

    const matrix = multiplySvgMatrices(parentMatrix, parseSvgTransformAttribute(element.getAttribute("transform")));
    const converted = convertSvgElement(element, matrix, presentation, defs, issues);
    if (!converted) {
      continue;
    }

    const convertedNodes = Array.isArray(converted) ? converted : [converted];
    if (element.getAttribute("data-curva-mask") === "true") {
      for (const node of convertedNodes) {
        node.isMask = true;
      }
    }
    if (pendingBackgroundBlur) {
      const target = convertedNodes.find(node => node.type === "primitive" || node.type === "vectorNetwork") ?? convertedNodes.at(-1);
      if (target) {
        target.style.effects = [...(target.style.effects ?? []), pendingBackgroundBlur];
      }
      pendingBackgroundBlur = undefined;
    }
    nodes.push(...convertedNodes);
  }

  return nodes;
}

function isCurvaExportFrameBackground(element: Element): boolean {
  return elementLocalName(element) === "rect" && element.getAttribute("id") === "frame";
}

function convertSvgElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): SceneNode | SceneNode[] | undefined {
  const localName = elementLocalName(element);
  const name = element.getAttribute("id") || localName;

  switch (localName) {
    case "g":
    case "svg": {
      const merged = element.hasAttribute("clip-path") || element.hasAttribute("mask") ? undefined : tryMergeExportedGraphicGroup(element, matrix, presentation, defs, issues);
      if (merged) {
        return merged;
      }
      const groupChildren = importSvgChildren(childElements(element), matrix, presentation, defs, issues);
      const clipMaskNodes = importReferencedMaskNodes(element, matrix, presentation, defs, issues);
      if (clipMaskNodes.length > 0) {
        groupChildren.unshift(...clipMaskNodes);
      }
      return createImportedGroupNode(name, groupChildren);
    }
    case "path":
      return convertPathElement(element, matrix, presentation, defs, issues, name);
    case "rect":
      return convertRectElement(element, matrix, presentation, defs, issues, name);
    case "circle":
      return convertCircleElement(element, matrix, presentation, defs, issues, name);
    case "ellipse":
      return convertEllipseElement(element, matrix, presentation, defs, issues, name);
    case "line":
      return convertLineElement(element, matrix, presentation, defs, issues, name);
    case "polyline":
      return convertPolylineElement(element, matrix, presentation, defs, issues, name, false);
    case "polygon":
      return convertPolylineElement(element, matrix, presentation, defs, issues, name, true);
    case "text":
      return convertTextElement(element, matrix, presentation, defs, issues, name);
    case "image":
      return convertImageElement(element, matrix, presentation, defs, issues, name);
    case "use":
      return convertUseElement(element, matrix, presentation, defs, issues);
    default:
      issues.add({ code: "unsupported-element", message: `<${localName}> is not supported yet`, element: localName });
      return undefined;
  }
}

function importReferencedMaskNodes(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): SceneNode[] {
  const maskElement = defs.resolveClipPath(element.getAttribute("clip-path")) ?? defs.resolveMask(element.getAttribute("mask"));
  if (!maskElement) {
    return [];
  }

  const maskMatrix = multiplySvgMatrices(matrix, parseSvgTransformAttribute(maskElement.getAttribute("transform")));
  const nodes = importSvgChildren(childElements(maskElement), maskMatrix, presentation, defs, issues);
  if (nodes.length === 0) {
    return [];
  }

  return nodes.map((node, index) => ({
    ...node,
    id: index === 0 ? node.id : createId("node"),
    name: node.name || "Mask",
    isMask: true,
  }));
}

function tryMergeExportedGraphicGroup(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): SceneNode | undefined {
  const children = childElements(element);
  const backgroundBlur = children
    .map(child => parseBackgroundBlurFromForeignObject(child))
    .find(effect => effect !== undefined);
  const paintStack = children.find(child => child.getAttribute("id")?.endsWith("_paintStack"));
  const geometryChildren = children.filter(child => isGeometryElement(child));

  if (paintStack) {
    return mergePaintStackGroup(paintStack, matrix, presentation, defs, issues, backgroundBlur);
  }

  if (geometryChildren.length === 1 && geometryChildren.length + (backgroundBlur ? 1 : 0) === children.length) {
    const node = convertGeometryElement(geometryChildren[0]!, matrix, presentation, defs, issues, element.getAttribute("id") ?? "shape");
    if (!node) {
      return undefined;
    }
    if (backgroundBlur) {
      node.style.effects = [...(node.style.effects ?? []), backgroundBlur];
    }
    return node;
  }

  return undefined;
}

function mergePaintStackGroup(
  paintStack: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  backgroundBlur?: ReturnType<typeof parseBackgroundBlurFromForeignObject>,
): SceneNode | undefined {
  const layers = childElements(paintStack);
  const firstLayer = layers[0];
  if (!firstLayer) {
    return undefined;
  }

  const node = convertGeometryElement(firstLayer, matrix, presentation, defs, issues, paintStack.getAttribute("id")?.replace(/_paintStack$/, "") ?? "shape");
  if (!node) {
    return undefined;
  }

  const fills = [];
  const strokes = [];
  const effects = [...(node.style.effects ?? [])];
  for (const layer of layers) {
    const layerStyle = nodeStyleFromSvgElement(layer, presentation, node.bounds, defs, issues);
    fills.push(...layerStyle.fills);
    if (layerStyle.strokes) {
      strokes.push(...layerStyle.strokes);
    }
    if (layerStyle.effects) {
      effects.push(...layerStyle.effects);
    }
  }

  node.style = {
    ...node.style,
    fills,
    strokes,
    stroke: strokes[0],
    effects: effects.length > 0 ? effects : undefined,
  };

  if (backgroundBlur) {
    node.style.effects = [...(node.style.effects ?? []), backgroundBlur];
  }

  return node;
}

function convertGeometryElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode | undefined {
  const localName = elementLocalName(element);
  switch (localName) {
    case "path":
      return convertPathElement(element, matrix, presentation, defs, issues, name);
    case "rect":
      return convertRectElement(element, matrix, presentation, defs, issues, name);
    case "circle":
      return convertCircleElement(element, matrix, presentation, defs, issues, name);
    case "ellipse":
      return convertEllipseElement(element, matrix, presentation, defs, issues, name);
    case "line":
      return convertLineElement(element, matrix, presentation, defs, issues, name);
    case "polyline":
      return convertPolylineElement(element, matrix, presentation, defs, issues, name, false);
    case "polygon":
      return convertPolylineElement(element, matrix, presentation, defs, issues, name, true);
    default:
      return undefined;
  }
}

function isGeometryElement(element: Element): boolean {
  const localName = elementLocalName(element);
  return localName === "path" || localName === "rect" || localName === "circle" || localName === "ellipse" || localName === "line" || localName === "polyline" || localName === "polygon";
}

function convertPathElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): VectorNetworkNode | undefined {
  const pathData = element.getAttribute("d");
  if (!pathData?.trim()) {
    return undefined;
  }

  const approximated: string[] = [];
  const network = pathDataToVectorNetwork(pathData, {
    reuseVertices: false,
    onApproximatedCommand: command => approximated.push(command),
  });
  for (const command of approximated) {
    issues.addOnce(
      "approximated-path-command",
      `Path command "${command}" was converted to cubic beziers`,
      "path",
    );
  }

  transformVectorNetwork(network, matrix);
  assignImportedVectorRegions(network, presentation);

  const node = createVectorNetworkNode(name, network);
  node.style = nodeStyleFromSvgElement(element, presentation, getVectorNetworkBounds(network), defs, issues);
  node.bounds = getVectorNetworkBounds(network);
  return node;
}

function convertRectElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode {
  const x = readSvgLength(element.getAttribute("x"));
  const y = readSvgLength(element.getAttribute("y"));
  const width = Math.max(0, readSvgLength(element.getAttribute("width")));
  const height = Math.max(0, readSvgLength(element.getAttribute("height")));
  const rx = readSvgLength(element.getAttribute("rx") ?? element.getAttribute("ry"));
  const ry = readSvgLength(element.getAttribute("ry") ?? element.getAttribute("rx"), rx);
  const topLeft = applySvgMatrix({ x, y }, matrix);
  const bottomRight = applySvgMatrix({ x: x + width, y: y + height }, matrix);
  const bounds = rectFromCorners(topLeft, bottomRight);

  const node = createRectangleNode(name, bounds.x, bounds.y, bounds.width, bounds.height);
  if (rx > 0 || ry > 0) {
    const radius = Math.max(rx, ry);
    node.primitive = { kind: "rectangle", cornerRadii: [radius, radius, radius, radius] };
  }
  return applyElementStyle(node, element, presentation, defs, issues);
}

function convertCircleElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode {
  const cx = readSvgLength(element.getAttribute("cx"));
  const cy = readSvgLength(element.getAttribute("cy"));
  const radius = readSvgLength(element.getAttribute("r"));
  const topLeft = applySvgMatrix({ x: cx - radius, y: cy - radius }, matrix);
  const bottomRight = applySvgMatrix({ x: cx + radius, y: cy + radius }, matrix);
  const bounds = rectFromCorners(topLeft, bottomRight);
  const node = createPrimitiveNode("oval", name, bounds.x, bounds.y, bounds.width, bounds.height);
  return applyElementStyle(node, element, presentation, defs, issues);
}

function convertEllipseElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode {
  const cx = readSvgLength(element.getAttribute("cx"));
  const cy = readSvgLength(element.getAttribute("cy"));
  const rx = readSvgLength(element.getAttribute("rx"));
  const ry = readSvgLength(element.getAttribute("ry"));
  const topLeft = applySvgMatrix({ x: cx - rx, y: cy - ry }, matrix);
  const bottomRight = applySvgMatrix({ x: cx + rx, y: cy + ry }, matrix);
  const bounds = rectFromCorners(topLeft, bottomRight);
  const node = createPrimitiveNode("oval", name, bounds.x, bounds.y, bounds.width, bounds.height);
  return applyElementStyle(node, element, presentation, defs, issues);
}

function convertLineElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode {
  const start = applySvgMatrix({
    x: readSvgLength(element.getAttribute("x1")),
    y: readSvgLength(element.getAttribute("y1")),
  }, matrix);
  const end = applySvgMatrix({
    x: readSvgLength(element.getAttribute("x2")),
    y: readSvgLength(element.getAttribute("y2")),
  }, matrix);
  const bounds = rectFromCorners(start, end);
  const node = createPrimitiveNode("line", name, bounds.x, bounds.y, Math.max(bounds.width, 1), Math.max(bounds.height, 1));
  return applyElementStyle(node, element, presentation, defs, issues);
}

function convertPolylineElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
  closed: boolean,
): VectorNetworkNode | undefined {
  const points = readSvgNumberList(element.getAttribute("points"));
  if (points.length < 4) {
    return undefined;
  }

  const pathData = [
    `M ${points[0]} ${points[1]}`,
    ...Array.from({ length: (points.length - 2) / 2 }, (_, index) => {
      const pointIndex = 2 + index * 2;
      return `L ${points[pointIndex]} ${points[pointIndex + 1]}`;
    }),
    closed ? "Z" : "",
  ].join(" ");

  const approximated: string[] = [];
  const network = pathDataToVectorNetwork(pathData, {
    reuseVertices: false,
    onApproximatedCommand: command => approximated.push(command),
  });
  for (const command of approximated) {
    issues.addOnce(
      "approximated-path-command",
      `Path command "${command}" was converted to cubic beziers`,
      closed ? "polygon" : "polyline",
    );
  }
  transformVectorNetwork(network, matrix);
  assignImportedVectorRegions(network, presentation);
  const node = createVectorNetworkNode(name, network);
  node.style = nodeStyleFromSvgElement(element, presentation, getVectorNetworkBounds(network), defs, issues);
  node.bounds = getVectorNetworkBounds(network);
  return node;
}

function convertImageElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): SceneNode | undefined {
  const paint = imagePaintFromImageElement(element, issues);
  if (!paint) {
    return undefined;
  }

  const x = readSvgLength(element.getAttribute("x"));
  const y = readSvgLength(element.getAttribute("y"));
  const width = Math.max(1, readSvgLength(element.getAttribute("width"), paint.intrinsicWidth));
  const height = Math.max(1, readSvgLength(element.getAttribute("height"), paint.intrinsicHeight));
  const topLeft = applySvgMatrix({ x, y }, matrix);
  const bottomRight = applySvgMatrix({ x: x + width, y: y + height }, matrix);
  const bounds = rectFromCorners(topLeft, bottomRight);

  const node = createRectangleNode(name, bounds.x, bounds.y, bounds.width, bounds.height);
  node.style = {
    ...createDefaultStyle(),
    fills: [normalizeImagePaint({ ...paint, fillMode: "fill", crop: { x: 0, y: 0, width: 1, height: 1 } })],
    strokes: [],
    opacity: presentation.opacity,
  };
  return node;
}

function convertUseElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): SceneNode | SceneNode[] | undefined {
  const href = element.getAttribute("href")
    ?? element.getAttribute("xlink:href")
    ?? element.getAttributeNS("http://www.w3.org/1999/xlink", "href");
  const targetId = href?.trim().replace(/^#/, "");
  if (!targetId) {
    issues.add({ code: "unsupported-use", message: "<use> is missing a target reference", element: "use" });
    return undefined;
  }

  const target = defs.getElementById(targetId);
  if (!target) {
    issues.add({ code: "missing-reference", message: `<use> reference "#${targetId}" was not found`, element: "use" });
    return undefined;
  }

  const x = readSvgLength(element.getAttribute("x"));
  const y = readSvgLength(element.getAttribute("y"));
  const useMatrix = multiplySvgMatrices(
    matrix,
    multiplySvgMatrices(
      parseSvgTransformAttribute(element.getAttribute("transform")),
      { a: 1, b: 0, c: 0, d: 1, e: x, f: y },
    ),
  );

  return convertSvgElement(target, useMatrix, presentation, defs, issues);
}

function convertTextElement(
  element: Element,
  matrix: SvgTransformMatrix,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
  name: string,
): TextNode {
  const x = readSvgLength(element.getAttribute("x"));
  const y = readSvgLength(element.getAttribute("y"));
  const anchor = element.getAttribute("text-anchor") ?? "start";
  const characters = collectTextContent(element);
  const position = applySvgMatrix({ x, y }, matrix);
  const node = createTextNode(name, position.x, position.y, characters);
  const fontFamily = resolveSvgFontFamily(
    presentation.fontFamily,
    fontCatalogFamilies(),
    issues,
    "text",
  );
  node.text.style.fontFamily = fontFamily;
  node.text.style.fontSize = presentation.fontSize ?? node.text.style.fontSize;
  node.text.style.fontWeight = presentation.fontWeight ?? node.text.style.fontWeight;
  node.text.style.horizontalAlign = anchor === "middle" ? "CENTER" : anchor === "end" ? "RIGHT" : "LEFT";
  const elementStyle = nodeStyleFromSvgElement(element, presentation, node.bounds, defs, issues);
  if (elementStyle.fills[0]?.type === "solid") {
    node.text.style.fill = elementStyle.fills[0];
    node.style = {
      ...elementStyle,
      fills: [],
    };
  } else {
    node.style = elementStyle;
  }
  node.transform = createIdentityTransform();
  node.bounds = {
    x: position.x,
    y: position.y,
    width: Math.max(4, characters.length * node.text.style.fontSize * 0.62),
    height: node.text.style.fontSize * 1.25,
  };
  return node;
}

function assignImportedVectorRegions(network: VectorNetwork, presentation: SvgPresentationContext): void {
  network.fillRule = presentation.fillRule;
  const contourCount = sampleNetworkRegionContours(network).length;
  if (contourCount > 1) {
    // Compound paths rely on fill-rule nesting at render time. Promoting every
    // subpath to a filled region makes inner contours paint as solid islands.
    network.regions = [];
    return;
  }

  network.regions = presentation.fill ? promoteVectorRegions(network, true) : [];
}

function createVectorNetworkNode(name: string, network: VectorNetworkNode["network"]): VectorNetworkNode {
  return {
    id: createId("node"),
    name,
    type: "vectorNetwork",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: getVectorNetworkBounds(network),
    style: createDefaultStyle("#000000"),
    network,
  };
}

function createImportedGroupNode(name: string, children: SceneNode[]): GroupNode | SceneNode | undefined {
  if (children.length === 0) {
    return undefined;
  }
  if (children.length === 1) {
    return children[0]!;
  }

  const bounds = unionSceneNodeBounds(children) ?? { x: 0, y: 0, width: 0, height: 0 };
  return {
    id: createId("group"),
    name,
    type: "group",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds,
    style: {
      fills: [],
      strokes: [],
      opacity: 1,
    },
    children,
  };
}

function wrapImportedNodes(nodes: SceneNode[], name: string): SceneNode[] {
  if (nodes.length === 0) {
    return [];
  }
  if (nodes.length === 1) {
    return nodes;
  }
  return [createImportedGroupNode(name, nodes) as GroupNode];
}

function applyElementStyle<T extends SceneNode>(
  node: T,
  element: Element,
  presentation: SvgPresentationContext,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): T {
  node.style = nodeStyleFromSvgElement(element, presentation, node.bounds, defs, issues);
  return node;
}

function transformVectorNetwork(network: VectorNetworkNode["network"], matrix: SvgTransformMatrix): void {
  if (matrix === IDENTITY_SVG_MATRIX) {
    return;
  }
  for (const vertex of network.vertices) {
    const transformed = applySvgMatrix(vertex, matrix);
    vertex.x = transformed.x;
    vertex.y = transformed.y;
  }
  for (const edge of network.edges) {
    if (edge.from.handle) {
      edge.from.handle = applySvgMatrix(edge.from.handle, matrix);
    }
    if (edge.to.handle) {
      edge.to.handle = applySvgMatrix(edge.to.handle, matrix);
    }
  }
}

function collectTextContent(element: Element): string {
  let text = "";
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === 3) {
      text += child.textContent ?? "";
    } else if (child.nodeType === 1) {
      text += collectTextContent(child as Element);
    }
  }
  return text;
}

function readViewBox(element: Element): Rect | undefined {
  const values = readSvgNumberList(element.getAttribute("viewBox"));
  if (values.length < 4) {
    return undefined;
  }
  return { x: values[0]!, y: values[1]!, width: values[2]!, height: values[3]! };
}

function rectFromCorners(topLeft: { x: number; y: number }, bottomRight: { x: number; y: number }): Rect {
  const x = Math.min(topLeft.x, bottomRight.x);
  const y = Math.min(topLeft.y, bottomRight.y);
  return {
    x,
    y,
    width: Math.max(0, Math.abs(bottomRight.x - topLeft.x)),
    height: Math.max(0, Math.abs(bottomRight.y - topLeft.y)),
  };
}

export function unionSceneNodeBounds(nodes: SceneNode[]): Rect | undefined {
  const rects = nodes.map(node => node.bounds).filter(bounds => bounds.width > 0 || bounds.height > 0);
  if (rects.length === 0) {
    return undefined;
  }
  const left = Math.min(...rects.map(bounds => bounds.x));
  const top = Math.min(...rects.map(bounds => bounds.y));
  const right = Math.max(...rects.map(bounds => bounds.x + bounds.width));
  const bottom = Math.max(...rects.map(bounds => bounds.y + bounds.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function offsetSceneNodes(nodes: SceneNode[], offset: { x: number; y: number }): SceneNode[] {
  return nodes.map(node => offsetSceneNode(node, offset));
}

function offsetSceneNode(node: SceneNode, offset: { x: number; y: number }): SceneNode {
  const next = structuredClone(node);
  next.bounds = {
    ...next.bounds,
    x: next.bounds.x + offset.x,
    y: next.bounds.y + offset.y,
  };
  if (next.type === "vectorNetwork") {
    for (const vertex of next.network.vertices) {
      vertex.x += offset.x;
      vertex.y += offset.y;
    }
  }
  if (next.type === "text") {
    next.transform = {
      ...next.transform,
      x: next.transform.x + offset.x,
      y: next.transform.y + offset.y,
    };
  }
  if ("children" in next && Array.isArray(next.children)) {
    next.children = next.children.map(child => offsetSceneNode(child, offset));
  }
  return next;
}
